import { useEffect, useMemo } from "react";
import { Box, Typography, IconButton, Paper, Grid, Button, Fade, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, EditCalendar } from "@mui/icons-material";
import { colors } from "../../ui/colors";
import { useCalendarDateSelectorState, useSetCalendarDateSelector } from "../states/useCalendarDateSelector";
import CardSelectionCalendar from "./CardSelectionCalendar";
import { CalendarMode } from "../types";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const QUARTERS = ["Q1 (Jan-Mar)", "Q2 (Apr-Jun)", "Q3 (Jul-Sep)", "Q4 (Oct-Dec)"];
const MODE_LABELS: Record<string, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

interface CalendarContentProps {
  allowFutureDates: boolean;
  width: number | string;
  height: string;
  onConfirm: () => void;
  externalCard?: boolean;
  showRangeOption?: boolean;
  hideRangeOnModes?: CalendarMode[];
  autoSelectCurrent?: boolean;
}

export function CalendarContent({
  allowFutureDates,
  width,
  height,
  onConfirm,
  externalCard = false,
  showRangeOption = true,
  hideRangeOnModes = [],
  autoSelectCurrent = false,
}: CalendarContentProps) {
  const { viewDate, range, mainMode, isRangePicker, showCard } = useCalendarDateSelectorState();
  const { setField, setPayload } = useSetCalendarDateSelector();
  const shouldShowRangeOption = showRangeOption && !hideRangeOnModes.includes(mainMode);
  const disableColor = "#9c9999";
  const activeTextColor = "#374151";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getCurrentRangeForMode = (mode: CalendarMode) => {
    const baseDate = new Date();

    if (mode === "day") {
      const start = new Date(baseDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(baseDate);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "week") {
      const start = new Date(baseDate);
      start.setDate(baseDate.getDate() - baseDate.getDay());
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "month") {
      const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "quarter") {
      const currentQuarter = Math.floor(baseDate.getMonth() / 3);
      const start = new Date(baseDate.getFullYear(), currentQuarter * 3, 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(baseDate.getFullYear(), currentQuarter * 3 + 3, 0);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "year") {
      const start = new Date(baseDate.getFullYear(), 0, 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(baseDate.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    return { start: null, end: null };
  };

  useEffect(() => {
    if (!autoSelectCurrent) return;
    if (isRangePicker) return;
    if (range.start && range.end) return;

    const currentRange = getCurrentRangeForMode(mainMode);

    setPayload({
      range: currentRange,
      isRangePicker: false,
      showCard: false,
    });
  }, [autoSelectCurrent, isRangePicker, mainMode, range.start, range.end, setPayload]);

  const isFuture = (date: Date) => !allowFutureDates && date > today;

  const weeksInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const weeks = [];
    let current = new Date(year, month, 1);
    current.setDate(current.getDate() - current.getDay());
    while (current.getMonth() <= month || (current.getMonth() === 11 && month === 11) || weeks.length < 4) {
      const start = new Date(current);
      const end = new Date(current);
      end.setDate(end.getDate() + 6);
      weeks.push({ start, end });
      current.setDate(current.getDate() + 7);
      if (current.getMonth() > month && current.getFullYear() >= year && weeks.length >= 4) break;
    }
    return weeks;
  }, [viewDate]);

  const daysInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) days.push(new Date(year, month, i));
    return days;
  }, [viewDate]);

  const getStatus = (blockStart: Date, blockEnd?: Date) => {
    if (!range.start) return "normal";
    const sTime = blockStart.getTime();
    const eTime = (blockEnd || blockStart).getTime();
    const rStart = range.start.getTime();
    const rEnd = range.end?.getTime();
    if (sTime === rStart) return "start";
    if (rEnd && eTime === rEnd) return "end";
    if (rEnd && sTime > rStart && eTime < rEnd) return "in-range";
    return "normal";
  };

  const handleSelection = (start: Date, end: Date) => {
    if (isRangePicker) {
      if (!range.start || (range.start && range.end)) {
        setField("range", { start, end: null });
      } else {
        const finalRange = start < range.start ? { start, end: range.start } : { start: range.start, end };
        setPayload({ range: finalRange, showCard: true });
      }
    } else {
      setPayload({ range: { start, end }, showCard: true });
    }
  };

  const handleReset = () => {
    setPayload({ range: { start: null, end: null }, showCard: false });
  };

  const handleRangePickerToggle = (isRange: boolean) => {
    setPayload({ isRangePicker: isRange, range: { start: null, end: null } });
  };

  useEffect(() => {
    if (!shouldShowRangeOption && isRangePicker) {
      setPayload({
        isRangePicker: false,
        range: { start: null, end: null },
        showCard: false,
      });
    }
  }, [shouldShowRangeOption, isRangePicker, setPayload]);

  return (
    <Paper
      sx={{ p: 2, width: "100%", height, minHeight: 300, borderRadius: 5, bgcolor: "var(--fx-surface-base, #FFFFFF)", display: "flex", flexDirection: "column" }}
      elevation={0}
    >
      {!showCard || externalCard ? (
        <Fade in={!showCard || externalCard}>
          <Box>
            {shouldShowRangeOption && (
              <Box sx={{ bgcolor: "var(--fx-surface-muted, #F1F5F9)", borderRadius: 3, p: 0.5, mb: 2 }}>
                <Grid container spacing={0.5}>
                  {[
                    { label: MODE_LABELS[mainMode], val: false },
                    { label: "Range", val: true },
                  ].map((opt) => {
                    const isSelected = isRangePicker === opt.val;

                    return (
                      <Grid item xs={6} key={opt.label}>
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => handleRangePickerToggle(opt.val)}
                          sx={{
                            borderRadius: 2.5,
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 12,
                            py: 0.4,
                            bgcolor: isSelected ? colors.orange.base : "transparent",
                            color: isSelected ? "white" : "#9ca3af",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: isSelected ? colors.orange.base : "rgba(255, 107, 0, 0.08)",
                            },
                            "&.MuiButton-root": {
                              transition: "all 0.2s ease-in-out",
                            },
                          }}
                        >
                          {opt.label}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
            {mainMode !== "year" && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <IconButton size="small" onClick={() => setField("viewDate", new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography sx={{ fontWeight: 800, fontSize: 12 }}>
                  {mainMode === "quarter" ? viewDate.getFullYear() : `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`}
                </Typography>
                <IconButton size="small" onClick={() => setField("viewDate", new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Grid container spacing={1}>
              {/* Week View */}
              {mainMode === "week" &&
                weeksInMonth.map((w, i) => {
                  const status = getStatus(w.start, w.end);
                  const active = status !== "normal";
                  const disabled = isFuture(w.start);
                  return (
                    <Grid item xs={12} key={i}>
                      <Tooltip title={disabled ? (allowFutureDates ? "" : "Future week") : ""} arrow>
                        <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                          <Button
                            fullWidth
                            disabled={disabled}
                            onClick={() => handleSelection(w.start, w.end)}
                            sx={{
                              py: 0.8,
                              borderRadius: 2,
                              px: 2,
                              textTransform: "none",
                              display: "flex",
                              justifyContent: "space-between",
                              bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                              color: active ? colors.orange.base : activeTextColor,
                              "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                              "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                            }}
                          >
                            <Typography sx={{ fontSize: 11, fontWeight: 800 }}>Week {i + 1}</Typography>
                            <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                              {w.start.getDate()} - {w.end.getDate()} {MONTHS[w.end.getMonth()].slice(0, 3)}
                            </Typography>
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  );
                })}

              {/* Month View */}
              {mainMode === "month" &&
                MONTHS.map((m, i) => {
                  const start = new Date(viewDate.getFullYear(), i, 1);
                  const status = getStatus(start, new Date(viewDate.getFullYear(), i + 1, 0));
                  const active = status !== "normal";
                  const disabled = isFuture(start);
                  return (
                    <Grid item xs={4} key={m}>
                      <Tooltip title={disabled ? (allowFutureDates ? "" : "Future month") : ""} arrow>
                        <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                          <Button
                            fullWidth
                            disabled={disabled}
                            onClick={() => handleSelection(start, new Date(viewDate.getFullYear(), i + 1, 0))}
                            sx={{
                              fontSize: 10,
                              fontWeight: 800,
                              bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                              color: active ? colors.orange.base : activeTextColor,
                              "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                              "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                            }}
                          >
                            {m.slice(0, 3)}
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  );
                })}

              {/* Day View */}
              {mainMode === "day" && (
                <Grid container columns={7} sx={{ mt: 1, px: 1 }}>
                  {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                    <Grid item xs={1} key={d} textAlign="center">
                      <Typography sx={{ fontWeight: 800, color: "#c7cbd0", fontSize: 11 }}>{d}</Typography>
                    </Grid>
                  ))}
                  {daysInMonth.map((date, i) => {
                    if (!date) return <Grid item xs={1} key={i} />;
                    const disabled = isFuture(date);
                    const status = getStatus(date);
                    const isSingleDaySelected = !isRangePicker && status === "start";
                    const isRangeSelected = isRangePicker && status !== "normal";
                    const isEndPoints = isRangePicker && (status === "start" || status === "end");
                    return (
                      <Grid item xs={1} key={i}>
                        <Tooltip title={disabled ? (allowFutureDates ? "" : "Future date") : ""} arrow>
                          <Box
                            onClick={() => !disabled && handleSelection(date, date)}
                            sx={{
                              height: 30,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: disabled ? "not-allowed" : "pointer",
                              fontSize: 11,
                              fontWeight: 800,
                              position: "relative",

                              backgroundColor: isSingleDaySelected ? colors.orange.base : isRangeSelected ? colors.orange[100] : disabled ? "#f9fafb" : "transparent",
                              borderRadius: isSingleDaySelected ? "8px" : 0,

                              color: isSingleDaySelected
                                ? "#fff"
                                : isRangeSelected
                                  ? colors.orange.base
                                  : disabled
                                    ? disableColor
                                    : activeTextColor,

                              border: disabled ? "1px solid var(--fx-surface-border, #E2E8F0)" : "none",

                              ...(isRangePicker &&
                                status === "start" && {
                                  borderTopLeftRadius: "8px",
                                  borderBottomLeftRadius: "8px",
                                }),

                              ...(isRangePicker &&
                                status === "end" && {
                                  borderTopRightRadius: "8px",
                                  borderBottomRightRadius: "8px",
                                }),
                            }}
                          >
                            {isSingleDaySelected && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  width: 26,
                                  height: 26,
                                  bgcolor: colors.orange.base,
                                  borderRadius: "8px",
                                  zIndex: 0,
                                }}
                              />
                            )}

                            {isEndPoints && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  width: 22,
                                  height: 22,
                                  bgcolor: colors.orange.base,
                                  borderRadius: "8px",
                                  zIndex: -1,
                                }}
                              />
                            )}
                            <Box
                              component="span"
                              sx={{
                                position: "relative",
                                zIndex: 1,
                                color: isSingleDaySelected ? "#fff" : "inherit",
                              }}
                            >
                              {date.getDate()}
                            </Box>
                          </Box>
                        </Tooltip>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              {/* Quarter View */}
              {mainMode === "quarter" &&
                QUARTERS.map((q, i) => {
                  const start = new Date(viewDate.getFullYear(), i * 3, 1);
                  const active = getStatus(start, new Date(viewDate.getFullYear(), (i + 1) * 3, 0)) !== "normal";
                  const disabled = isFuture(start);
                  return (
                    <Grid item xs={6} key={q}>
                      <Tooltip title={disabled ? (allowFutureDates ? "" : "Future quarter") : ""} arrow>
                        <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                          <Button
                            fullWidth
                            disabled={disabled}
                            onClick={() => handleSelection(start, new Date(viewDate.getFullYear(), (i + 1) * 3, 0))}
                            sx={{
                              py: 1,
                              borderRadius: 2,
                              textTransform: "none",
                              display: "flex",
                              flexDirection: "column",
                              bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                              color: active ? colors.orange.base : activeTextColor,
                              "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                              "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                            }}
                          >
                            <Typography sx={{ fontSize: 11, fontWeight: 900 }}>{q.split(" ")[0]}</Typography>
                            <Typography sx={{ fontSize: 9, fontWeight: 700, opacity: 0.8 }}>
                              {MONTHS[start.getMonth()].slice(0, 3)} - {MONTHS[i * 3 + 2].slice(0, 3)}
                            </Typography>
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  );
                })}

              {/* Year View */}
              {mainMode === "year" &&
                Array.from({ length: 9 }, (_, i) => viewDate.getFullYear() - 4 + i).map((y) => {
                  const start = new Date(y, 0, 1);
                  const active = getStatus(start, new Date(y, 11, 31)) !== "normal";
                  const disabled = isFuture(start);
                  return (
                    <Grid item xs={4} key={y}>
                      <Tooltip title={disabled ? (allowFutureDates ? "" : "Future year") : ""} arrow>
                        <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                          <Button
                            fullWidth
                            disabled={disabled}
                            onClick={() => handleSelection(start, new Date(y, 11, 31))}
                            sx={{
                              py: 1,
                              borderRadius: 2,
                              textTransform: "none",
                              display: "flex",
                              flexDirection: "column",
                              bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                              color: active ? colors.orange.base : activeTextColor,
                              "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                              "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                            }}
                          >
                            <Typography sx={{ fontSize: 11, fontWeight: 900 }}>{y}</Typography>
                            <Typography sx={{ fontSize: 8, fontWeight: 700, opacity: 0.8 }}>Full Year</Typography>
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  );
                })}
            </Grid>
          </Box>
        </Fade>
      ) : (
        <CardSelectionCalendar showCard={showCard} setField={setField} onConfirm={onConfirm} handleReset={handleReset} />
      )}
    </Paper>
  );
}
